import { useEffect, useMemo, useState } from 'react'
import { InputBase, styled } from '@mui/material'
import ExpandLessRounded from '@mui/icons-material/ExpandLessRounded'
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded'
import { intl } from '../../../../../../i18n'
import { colorPalette } from '../../../../../style/colorPalette'
import { useProjectPrivateContext } from '../../../../../context/projectContext'
import { RadioButton } from '../../../../../components/radio-button'
import {
  CollapsibleOpenState,
  CollapsibleSelectField,
} from '../components/CollapsibleSelectField'
import { EllipsisText } from '../components/EllipsisText'
import { useTicketListSelectData } from './hooks/useTicketListSelectData'
import {
  useTicketListGroups,
  TicketListGroup,
} from './hooks/useTicketListGroups'

type TicketListSelectProps = {
  snapshotUuid: string
  value: string[]
  onChange: (value: string[]) => void
  height?: string
  // 工程との切替でコンポーネントが作り直されても開閉状態を保つため、呼び出し側で状態を持つ
  openState?: CollapsibleOpenState
}

export const TicketListSelect = ({
  snapshotUuid,
  value,
  onChange,
  height = '400px',
  openState,
}: TicketListSelectProps) => {
  const [searchText, setSearchText] = useState('')
  // デフォルトは全グループ折りたたみ。ユーザーが明示的に開いたものだけを記録する
  const [expandedTypes, setExpandedTypes] = useState<Set<string>>(new Set())

  const { rows, pathTextByUuid, isLoading } = useTicketListSelectData({
    snapshotUuid,
  })
  const { ticketTypes } = useProjectPrivateContext()
  const groups = useTicketListGroups(rows, searchText, ticketTypes)

  const isSearching = searchText.trim().length > 0
  const filteredCount = useMemo(
    () => groups.reduce((sum, group) => sum + group.items.length, 0),
    [groups]
  )
  const selectedUuid = value[0]
  // 選択済みの項目が閉じたグループに隠れて見えなくならないよう、そのグループを開く。
  // 描画条件ではなく state に反映することで、ユーザーが後から折りたためる。
  // 依存は value 配列そのものではなく選択中の uuid にする(親は毎描画で新しい配列を渡すため、
  // 配列を依存にするとユーザーが畳んだグループが再描画のたびに開き直してしまう)
  useEffect(() => {
    const selectedTicketType = rows.find(
      row => row.uuid === selectedUuid
    )?.ticketType
    if (!selectedTicketType) return
    setExpandedTypes(prev =>
      prev.has(selectedTicketType)
        ? prev
        : new Set(prev).add(selectedTicketType)
    )
  }, [rows, selectedUuid])

  const handleSelect = (uuid: string) => onChange([uuid])

  const handleSearchTextChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setSearchText(e.target.value)

  const handleToggleGroup = (ticketType: string) => {
    setExpandedTypes(prev => {
      const next = new Set(prev)
      if (next.has(ticketType)) {
        next.delete(ticketType)
      } else {
        next.add(ticketType)
      }
      return next
    })
  }

  const selectedRow = useMemo(
    () => rows.find(row => row.uuid === selectedUuid),
    [rows, selectedUuid]
  )
  const chips =
    selectedUuid && selectedRow
      ? [
          {
            key: selectedUuid,
            label: selectedRow.displayName,
            iconUrl: ticketTypes.find(
              type => type.code === selectedRow.ticketType
            )?.iconUrl,
          },
        ]
      : []

  return (
    <CollapsibleSelectField
      chips={chips}
      emptyLabel={intl.formatMessage({ id: 'versionReport.treeSelect.empty' })}
      openState={openState}
    >
      <Container style={{ height }}>
        <SearchInput
          placeholder={intl.formatMessage({
            id: 'versionReport.ticketListSelect.searchPlaceholder',
          })}
          value={searchText}
          onChange={handleSearchTextChange}
        />
        <GroupList>
          {isLoading ? (
            <LoadingContainer>
              <Spinner />
            </LoadingContainer>
          ) : groups.length === 0 ? (
            <EmptyState>
              {intl.formatMessage({
                id: 'versionReport.ticketListSelect.noResults',
              })}
            </EmptyState>
          ) : (
            groups.map(group => (
              <GroupSection
                key={group.ticketType}
                group={group}
                pathTextByUuid={pathTextByUuid}
                selectedUuid={value[0]}
                // 検索中はマッチ結果を見せるため常に展開する
                isExpanded={isSearching || expandedTypes.has(group.ticketType)}
                onToggleGroup={handleToggleGroup}
                onSelect={handleSelect}
              />
            ))
          )}
        </GroupList>
        <FooterContainer>
          <CountLabel>
            <CountNumerator>
              {filteredCount}
              {intl.formatMessage({ id: 'versionReport.treeSelect.count' })}
            </CountNumerator>
            {' / '}
            {rows.length}
            {intl.formatMessage({ id: 'versionReport.treeSelect.count' })}
          </CountLabel>
        </FooterContainer>
      </Container>
    </CollapsibleSelectField>
  )
}

type GroupSectionProps = {
  group: TicketListGroup
  pathTextByUuid: ReadonlyMap<string, string>
  selectedUuid: string | undefined
  isExpanded: boolean
  onToggleGroup: (ticketType: string) => void
  onSelect: (uuid: string) => void
}

const GroupSection = ({
  group,
  pathTextByUuid,
  selectedUuid,
  isExpanded,
  onToggleGroup,
  onSelect,
}: GroupSectionProps) => (
  <div>
    <GroupHeader onClick={() => onToggleGroup(group.ticketType)}>
      {isExpanded ? (
        <ExpandLessRounded
          sx={{ fontSize: 16, color: colorPalette.monotone[4] }}
        />
      ) : (
        <ExpandMoreRounded
          sx={{ fontSize: 16, color: colorPalette.monotone[4] }}
        />
      )}
      {group.iconUrl && <GroupIcon src={group.iconUrl} />}
      <GroupName>{group.typeName}</GroupName>
      <GroupCount>
        {group.items.length}
        {intl.formatMessage({ id: 'versionReport.treeSelect.count' })}
      </GroupCount>
    </GroupHeader>
    {isExpanded &&
      group.items.map(item => {
        const pathText = pathTextByUuid.get(item.uuid)
        return (
          <OptionItem key={item.uuid} onClick={() => onSelect(item.uuid)}>
            <RadioButton
              checked={item.uuid === selectedUuid}
              onChange={() => onSelect(item.uuid)}
              size="s"
            />
            <OptionText>
              <OptionName>{item.displayName}</OptionName>
              {pathText && <PathText>{pathText}</PathText>}
            </OptionText>
          </OptionItem>
        )
      })}
  </div>
)

const Container = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  border: `1px solid ${colorPalette.monotone[2]}`,
  borderRadius: '4px',
  background: colorPalette.monotone[0],
  padding: '10px',
  width: '100%',
})

const SearchInput = styled(InputBase)({
  border: `1px solid ${colorPalette.monotone[2]}`,
  borderRadius: '4px',
  padding: '4px 8px',
  marginBottom: '8px',
  fontSize: '14px',
  width: '100%',
})

const GroupList = styled('div')({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  overflowY: 'auto',
  overflowAnchor: 'none',
})

const GroupHeader = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '6px 4px',
  position: 'sticky',
  top: 0,
  zIndex: 1,
  background: colorPalette.monotone[0],
  cursor: 'pointer',
})

const GroupIcon = styled('img')({
  width: '16px',
  height: '16px',
  flexShrink: 0,
})

const GroupName = styled('span')({
  fontSize: '12px',
  fontWeight: 'bold',
  color: colorPalette.monotone[4],
})

const GroupCount = styled('span')({
  fontSize: '11px',
  fontWeight: 'normal',
  color: colorPalette.monotone[3],
})

const OptionItem = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  cursor: 'pointer',
  borderRadius: '4px',
  padding: '2px 4px 2px 20px',
  '&:hover': {
    background: colorPalette.monotone[1],
  },
})

const OptionText = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
})

const OptionName = styled('span')({
  fontSize: '13px',
  color: colorPalette.monotone[10],
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
})

const PathText = styled(EllipsisText)({
  fontSize: '11px',
  lineHeight: 1.4,
  color: colorPalette.monotone[4],
})

const EmptyState = styled('div')({
  fontSize: '12px',
  color: colorPalette.monotone[4],
  padding: '10px 4px',
  textAlign: 'center',
})

const LoadingContainer = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '80px',
})

const Spinner = styled('div')({
  width: '28px',
  height: '28px',
  border: `3px solid ${colorPalette.monotone[2]}`,
  borderTop: `3px solid ${colorPalette.skyBlue[7]}`,
  borderRadius: '50%',
  animation: 'ticketListSelectSpin 0.8s linear infinite',
  '@keyframes ticketListSelectSpin': {
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' },
  },
})

const FooterContainer = styled('div')({
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  marginTop: '8px',
})

const CountLabel = styled('span')({
  fontSize: '12px',
  color: colorPalette.monotone[3],
})

const CountNumerator = styled('span')({
  fontWeight: 'bold',
  color: colorPalette.monotone[5],
})
